/* eslint-disable complexity, id-length */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '@godaddy/react-mintl';
import classNames from 'classnames';
import AsyncIcon from '@ux/icon/async-icon.js';
import OrderConfirmation from '@ux/order-confirmation';
import AccountInfo from './AccountInfo/AccountInfo';
import HelpLink from './HelpLink';
import SidebarDesktop from './SidebarDesktop';
import SidebarMobile from './SidebarMobile';
import withPostponedRender from '../lib/withPostponedRender';
import CartWrapper from './CartWrapper';
import ProfileAvatar from './ProfileAvatar';
import AppSwitcher from './AppSwitcher';
import NavigationLink from './NavigationLink';
import { SSRSafeSuspense } from '@ux/header-util';

import { partnerEid } from '../lib/requestedHeaderEids';
import { addClassToElement, removeClassFromElement } from '../lib/classUtils';
import { useUserData, useRootProps } from './UserDataProvider';

import './Sidebar-base.css';
import './Sidebar-ns.css';
import './Sidebar.css';

import useIsMobile from '../lib/useIsMobile';
import useLogoHref from '../lib/useLogoHref';

import InPageNav from './InPageNav/InPageNav';
import { TYPES } from '../lib/types';

const BellNotificationWrapper = React.lazy(() =>
  import(
    /* webpackChunkName: "bell-notifications-wrapper" */ './BellNotificationWrapper'
  )
);
const OptOutModalContainer = React.lazy(() =>
  import(
    /* webpackChunkName: "opt-out-container" */ './OptOutModalContainer'
  )
);

const Sidebar = ({
  app,
  requestedHeader,
  appEid,
  sidebarNav,
  accountTrayNav,
  footerContent,
  navigation,
  isAfternic,
  isReseller,
  navHeading,
  navSubHeadline,
  navBreadcrumb,
  afterNavHeadingLeft,
  beforeNavHeading,
  afterNavHeadingRight,
  sidebarNavLinkHandler,
  primaryActiveNavLink,
  accountTrayBottomNav,
  navigationTop,
  cartItems,
  cartLink,
  CartComponent,
  customNotifications,
  customWaffleLinks,
  helpUrl,
  showGlobalUtilityNavCart,
  showGlobalUtilityNavHelpLink,
  showGlobalUtilityNavNotifications,
  showGlobalUtilityNavWaffle,
  sidebarNavTopComponent,
  showHubBanner,
  inPageNavContainerClass
}) => {
  const [accountInfoOpen, setAccountInfoOpen] = useState(false);
  const { userDataLoaded, userData } = useUserData();
  const { loggedIn } = userData;
  const { orderId, urls, features, split } = useRootProps();
  const isMobile = useIsMobile();
  const logoHref = useLogoHref();
  const isPartners = requestedHeader === TYPES.partners;
  const showProfileAvatar = userDataLoaded && loggedIn && !isAfternic;
  const showNotifications = userDataLoaded && loggedIn && !isAfternic && showGlobalUtilityNavNotifications;
  const showCustomWaffleMenu = customWaffleLinks?.topLinks?.length || customWaffleLinks?.quickLinks?.length;

  const openAccountInfo = useCallback(() => {
    setAccountInfoOpen(true);
  }, []);
  const closeAccountInfo = useCallback(() => {
    setAccountInfoOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      removeClassFromElement('body', 'sidebar-mobile');
    } else {
      addClassToElement('body', 'sidebar-mobile');
    }
  }, [isMobile]);

  let navTop = [];
  let navTopContent = false;

  const pageEid = appEid.extend('global_utility_nav');
  const accountInfoButtonEid = pageEid(`account_info.button.click`) || '';

  if (Array.isArray(navigationTop)) {
    let filtered = [];
    if (navigationTop) {
      const includeItem = isMobile
        ? (item) => !(item && item.desktopOnly)
        : (item) => !(item && item.mobileOnly);
      filtered = navigationTop.filter(includeItem);
    }

    navTop = filtered.map((item, idx) => {
      if (React.isValidElement(item)) {
        return (
          <span className='nav-top-link global-nav-margin' key={ item.key || `custom-nav-${idx}` }>
            { item }
          </span>
        );
      }
      if (item && item.caption && item.href) {
        return (
          <NavigationLink
            link={ item }
            className='nav-top-link global-nav-margin'
            key={ item.caption }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
          >
            {item.caption}
          </NavigationLink>
        );
      }
      return null;
    }).filter(Boolean);
    navTopContent = navTop.length > 0;
  } else if (React.isValidElement(navigationTop)) {
    navTop = navigationTop;
    navTopContent = true;
  }

  const nearStarSplit = split?.includes('nearstar') ? 'sidebar-nearstar' : 'sidebar-normal';

  // Add class to body for nearstar split, will remove once it becomes default
  if (split?.includes('nearstar') && !document.body.classList.contains('nearstar-split')) {
    addClassToElement('body', 'nearstar-split');
  }

  return (
    <>
      <div className={ classNames('nav-wrapper', {
        'hub-banner-padding': showHubBanner }, nearStarSplit) }>
        {(!isMobile) ?
          <SidebarDesktop
            navigation={ sidebarNav }
            requestedHeader={ requestedHeader }
            logoHref={ logoHref }
            footerContent={ footerContent }
            isAfternic={ isAfternic }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
            primaryActiveNavLink={ primaryActiveNavLink }
            sidebarNavTopComponent={ sidebarNavTopComponent }
            showHubBanner={ showHubBanner }
            isReseller={ isReseller }
          /> :
          <SidebarMobile
            navigation={ sidebarNav }
            logoHref={ logoHref }
            footerContent={ footerContent }
            isAfternic={ isAfternic }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
            primaryActiveNavLink={ primaryActiveNavLink }
            sidebarNavTopComponent={ sidebarNavTopComponent }
            showHubBanner={ showHubBanner }
          />
        }
        {orderId && <OrderConfirmation orderId={ orderId } />}

        <div className='global-utility-nav'>
          { navTopContent ?
            <>
              <div className='top-nav-container'>{ navTop }</div>
              <div className='custom-links-divider global-nav-margin' />
            </> : ''
          }
          { showGlobalUtilityNavHelpLink && (
            <HelpLink appEid={ appEid } helpUrl={ helpUrl } />
          ) }
          { showNotifications && (
            <SSRSafeSuspense>
              <BellNotificationWrapper app={ app } customNotifications={ customNotifications } />
            </SSRSafeSuspense>
          )}
          { requestedHeader === TYPES.partners && (
            <SSRSafeSuspense>
              <OptOutModalContainer appEid={ partnerEid } />
            </SSRSafeSuspense>
          )}
          { (showCustomWaffleMenu || showGlobalUtilityNavWaffle) &&
            <AppSwitcher
              appEid={ appEid }
              urls={ urls }
              customWaffleLinks={ customWaffleLinks }
              showWaffleCommerceLink={ features?.showWaffleCommerceLink }
            />
          }
          <FormattedMessage id='Shared:AccountTray:CustomerInfo'>
            {accountButtonAriaLabel => (
              <button
                aria-label={ accountButtonAriaLabel }
                type='button'
                data-eid={ accountInfoButtonEid }
                data-testid='account-info-btn'
                onClick={ openAccountInfo }
                className={ classNames('account-info-button global-nav-margin', {
                  'active': accountInfoOpen,
                  'icon-only': !loggedIn || isReseller }) }
              >
                { (showProfileAvatar && !isReseller) ?
                  <ProfileAvatar showAvatarImage={ isPartners } /> :
                  <AsyncIcon icon='user-circle' /> }
              </button>
            )}
          </FormattedMessage>
          <AccountInfo
            loggedIn={ loggedIn }
            isOpen={ accountInfoOpen }
            navigation={ accountTrayNav }
            accountTrayBottomNav={ accountTrayBottomNav }
            onClose={ closeAccountInfo }
            appEid={ appEid }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
            isPartners={ isPartners }
          />
          { showGlobalUtilityNavCart && CartComponent != null &&
          <CartWrapper
            app={ app }
            appEid={ appEid }
            cartItems={ cartItems }
            cartLink={ cartLink }
            CartComponent={ CartComponent }
          />
          }
        </div>
      </div>
      <InPageNav
        navigation={ navigation }
        navHeading={ navHeading }
        navSubHeadline={ navSubHeadline }
        navBreadcrumb={ navBreadcrumb }
        afterNavHeadingLeft={ afterNavHeadingLeft }
        beforeNavHeading={ beforeNavHeading }
        afterNavHeadingRight={ afterNavHeadingRight }
        inPageNavContainerClass={ inPageNavContainerClass }
      />
    </>
  );
};

Sidebar.propTypes = {
  app: PropTypes.string.isRequired,
  requestedHeader: PropTypes.symbol.isRequired,
  appEid: PropTypes.func,
  accountTrayNav: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  accountTrayBottomNav: PropTypes.node,
  customNotifications: PropTypes.arrayOf(PropTypes.object),
  footerContent: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  isAfternic: PropTypes.bool,
  isReseller: PropTypes.bool,
  cartItems: PropTypes.number,
  CartComponent: PropTypes.node,
  cartLink: PropTypes.string,
  helpUrl: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string
  }),
  navigation: PropTypes.arrayOf(PropTypes.object),
  navHeading: PropTypes.shape({
    text: PropTypes.string
  }),
  navSubHeadline: PropTypes.oneOfType([
    PropTypes.shape({
      text: PropTypes.string
    }),
    PropTypes.node
  ]),
  navBreadcrumb: PropTypes.shape({
    title: PropTypes.string,
    href: PropTypes.string,
    eid: PropTypes.string,
    onClick: PropTypes.func
  }),
  afterNavHeadingLeft: PropTypes.node,
  beforeNavHeading: PropTypes.node,
  afterNavHeadingRight: PropTypes.node,
  navigationTop: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.node
    ])),
    PropTypes.node
  ]),
  showGlobalUtilityNavHelpLink: PropTypes.bool,
  showGlobalUtilityNavWaffle: PropTypes.bool,
  showGlobalUtilityNavCart: PropTypes.bool,
  sidebarNav: PropTypes.arrayOf(PropTypes.object),
  sidebarNavLinkHandler: PropTypes.func,
  split: PropTypes.string,
  primaryActiveNavLink: PropTypes.string,
  customWaffleLinks: PropTypes.PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.objectOf(PropTypes.string)
    )
  ),
  sidebarNavTopComponent: PropTypes.node,
  showHubBanner: PropTypes.bool,
  inPageNavContainerClass: PropTypes.string,
  showGlobalUtilityNavNotifications: PropTypes.bool
};

export default withPostponedRender(Sidebar);
